import React from "react";
import FindProgress from "../../../components/FindProgress";
import { useFeatures } from "../../../utils/features";
import { batteryLabel, chargeBadge, lastSeenLabel } from "../../../utils/beacons";
import { lotBeaconWhere, useLotBeacon } from "../../../features/materials/lotBeacons";
import "../../mobile.css";

// Pallet beacons on the phone Materials screens. Layout only: the reads and Find are
// features/materials/lotBeacons.js (the desktop TRK popup's BeaconCard runs on the same hook).

/** A charge badge's desktop class -> the phone tag's look. */
export const BADGE_TONE = { "bg-danger": "jq-tag-block", "bg-warning text-dark": "jq-tag-warn" };

/**
 * One lot's beacon on the phone lot panel: which beacon, its battery, where it was last heard, and a
 * big Find button with the find's progress (and the shop map, when that is on) under it. No beacon:
 * says so and how to put one on. Nothing at all while pallet beacons are switched off.
 */
export function LotBeaconCard({ mtnId }) {
  const features = useFeatures();
  const enabled = features.palletBeacons.enabled;
  const { data, beacon, loadError, busy, msg, load, find, finder, finding, activeFind } = useLotBeacon(mtnId, enabled);
  if (!enabled) return null;

  const badge = chargeBadge(beacon);
  const where = lotBeaconWhere(beacon, features.palletBeacons.location);
  const heard = beacon
    ? (beacon.docked_station ? `Plugged in at ${beacon.docked_station}` : beacon.in_range ? "In range" : `Last heard ${lastSeenLabel(beacon.last_seen_at)}`)
    : "";

  return (
    <section className="jq-card mt-2" data-testid="lot-beacon">
      {!data && !loadError && <div className="jq-muted">Loading beacon…</div>}
      {loadError && !data && <div className="jq-muted">Beacon info unavailable: {loadError}</div>}
      {beacon && (
        <>
          <div className="d-flex align-items-center flex-wrap gap-2">
            <span className="fw-bold font-monospace" data-testid="lot-beacon-id">{beacon.beacon_id}</span>
            <span className={badge ? "fw-bold" : ""}>{batteryLabel(beacon)}</span>
            {beacon.charging ? <span className="jq-tag jq-tag-next">charging</span> : null}
            {badge && <span className={`jq-tag ${BADGE_TONE[badge.className] || ""}`}>{badge.text}</span>}
          </div>
          <div className="jq-muted">{heard}</div>
          {where && <div className="jq-muted" data-testid="lot-beacon-where">{where}</div>}
          <button
            type="button"
            className="btn btn-success btn-lg w-100 mt-2"
            disabled={busy || finding}
            onClick={find}
          >
            <i className="fas fa-lightbulb me-2" aria-hidden="true" />
            {busy || finding ? "Finding…" : `Find ${beacon.beacon_id}`}
          </button>
          {activeFind && (
            <FindProgress find={activeFind} onRetry={find} onChange={finder.track(beacon.beacon_id)}
              onSettled={load} className="mt-2" />
          )}
          {beacon.lot_done && (
            <div className="jq-muted mt-1">This lot is done: the beacon frees itself the next time a gateway hears it.</div>
          )}
        </>
      )}
      {data && !beacon && (
        data.lot_done
          ? <div className="jq-muted">This lot is done: no beacon needed.</div>
          : <div className="jq-muted" data-testid="lot-beacon-none">No beacon on this pallet. To put one on, scan a free beacon's QR, or use the desktop TRK popup.</div>
      )}
      {msg && (
        <div className={`jq-note ${msg.tone === "danger" ? "jq-note-danger" : "jq-note-info"} mt-2`} role="status">{msg.text}</div>
      )}
    </section>
  );
}
